// Tüm API isteklerine oturum token'ı + oturum süresi kontrolü — TEK yerden, modül yüklenirken kurulur.
//
// Neden useEffect'te değil: React önce en alttaki bileşenlerin effect'lerini çalıştırır; sayfaların ilk veri istekleri
// (Dashboard, SeasonSelect…) AuthProvider'ın effect'inden ÖNCE gider. Sarmalayıcı modül yüklenirken kurulunca hiçbir
// istek kaçmaz. DataProvider/SeasonProvider AuthProvider'ın dışında olduğu için token'a zaten erişemiyordu.
//
// 1) /api/ isteğinde Authorization yoksa (ya da "Bearer undefined/null" ise) localStorage['ramada_user'].token eklenir.
//    /api/login'e eklenmez.
// 2) Oturum açıkken sunucu 401 ya da "token" mesajlı 403 dönerse: oturum kapatılır, uyarı gösterilir, /login'e gidilir.
//    403 "Bu işlem için yetkiniz yok" (izin kontrolü) oturum sorunu DEĞİLDİR. Oturum zaten kapalıysa (çıkış logu) uyarı yok.
const STORAGE_KEY = 'ramada_user';
const API_ORIGINS = ['http://localhost:5000'];

const storedToken = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null')?.token || '';
  } catch {
    return '';
  }
};

const urlOf = (input) => (typeof input === 'string' ? input : (input?.url || String(input || '')));

const isApiRequest = (url) => {
  try {
    const u = new URL(url, window.location.origin);
    const sameOrigin = u.origin === window.location.origin || API_ORIGINS.includes(u.origin);
    return sameOrigin && u.pathname.startsWith('/api/');
  } catch {
    return false;
  }
};

const isLoginRequest = (url) => {
  try {
    return new URL(url, window.location.origin).pathname === '/api/login';
  } catch {
    return false;
  }
};

const EMPTY_BEARER = /^Bearer\s*(undefined|null)?\s*$/i;

// init.headers'a token ekler; geçerli bir Authorization zaten varsa dokunmaz
const withToken = (init, token) => {
  const headers = init?.headers;
  if (headers instanceof Headers) {
    const current = headers.get('Authorization');
    if (current && !EMPTY_BEARER.test(current)) return init;
    const next = new Headers(headers);
    next.set('Authorization', `Bearer ${token}`);
    return { ...init, headers: next };
  }
  if (Array.isArray(headers)) {
    const current = headers.find(([k]) => /^authorization$/i.test(k));
    if (current && !EMPTY_BEARER.test(current[1])) return init;
    return { ...init, headers: [...headers.filter(([k]) => !/^authorization$/i.test(k)), ['Authorization', `Bearer ${token}`]] };
  }
  const obj = headers && typeof headers === 'object' ? headers : {};
  const key = Object.keys(obj).find((k) => /^authorization$/i.test(k));
  if (key && obj[key] && !EMPTY_BEARER.test(String(obj[key]))) return init;
  const rest = { ...obj };
  if (key) delete rest[key];
  return { ...(init || {}), headers: { ...rest, Authorization: `Bearer ${token}` } };
};

// İstekte gerçekten gönderilen token ('' = yok)
const bearerOf = (headers) => {
  let value = '';
  if (headers instanceof Headers) value = headers.get('Authorization') || '';
  else if (Array.isArray(headers)) value = (headers.find(([k]) => /^authorization$/i.test(k)) || [])[1] || '';
  else if (headers && typeof headers === 'object') {
    const key = Object.keys(headers).find((k) => /^authorization$/i.test(k));
    value = key ? String(headers[key] || '') : '';
  }
  const m = /^Bearer\s+(.+)$/i.exec(value.trim());
  return m && !/^(undefined|null)$/i.test(m[1]) ? m[1] : '';
};

const isTokenProblem = async (response) => {
  if (response.status === 401) return true;
  if (response.status !== 403) return false;
  try {
    const body = await response.clone().json();
    return /token/i.test(body?.message || '');
  } catch {
    return false;
  }
};

export const installFetchSession = () => {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function' || window.__ramadaFetchSession) return;
  window.__ramadaFetchSession = true;
  const originalFetch = window.fetch.bind(window);
  let handled = false;

  window.fetch = async (input, init) => {
    const url = urlOf(input);
    const api = isApiRequest(url);
    let nextInit = init;
    if (api && !isLoginRequest(url) && typeof input === 'string') {
      const token = storedToken();
      if (token) nextInit = withToken(init, token);
    }
    const sentToken = bearerOf(nextInit?.headers);
    const response = await originalFetch(input, nextInit);
    try {
      if (!handled && api && !isLoginRequest(url) && await isTokenProblem(response)) {
        const current = storedToken();
        if (current && sentToken && sentToken !== current) {
          // Bu sekme eski token'la çalışıyor ama başka sekmede yeniden giriş yapılmış: YENİ oturumu silme,
          // yalnızca bu sekmeyi yeni oturumla yeniden yükle.
          handled = true;
          window.location.reload();
        } else if (current) {
          handled = true;
          localStorage.removeItem(STORAGE_KEY);
          alert('Oturumunuzun süresi doldu. Lütfen tekrar giriş yapın.');
          window.location.assign('/login');
        }
        // current yoksa oturum zaten kapalı (çıkış logu ya da başka sekmede çıkış): uyarı yok
      }
    } catch { /* araya giren kod isteği hiçbir koşulda bozmasın */ }
    return response;
  };
};

// Test için iç parçalar
export const __test = { withToken, isApiRequest, isLoginRequest, bearerOf };
